/**
 * L'événement en attente — composant de dialogue **maison** (SPEC-UI §3.6, §7.1).
 *
 * Un `pending_event` **bloque toute autre action** (SPEC-MOTEUR §2.3, arbitrage A24) : la
 * modale est donc volontairement sans échappatoire — pas d'Échap, pas de croix. Le REPL M1
 * avait appris la leçon à ses dépens (« sinon le jeu a l'air cassé »).
 *
 * ⟦A-15 §4⟧ **La même modale sert les opportunités**, et c'est le point : côté moteur, une offre
 * à fenêtre EST un événement de `content/events.json` — mêmes conditions, mêmes choix, mêmes
 * effets, même action `choix_evenement`. Un seul champ les sépare (`fenetre_h`), et une seule
 * chose change à l'écran : **elle ne bloque pas**. Elle attend sur la table pendant que le monde
 * tourne, donc elle se ferme d'un « Plus tard » ou d'Échap, et elle affiche le temps qui reste.
 * On ne construit pas un second système de modales pour ça.
 *
 * Les choix affichés sont filtrés comme le contenu le demande (SPEC-CONTENU §3.3) : un choix
 * porteur de `skill_requis` n'apparaît que si le nœud est possédé, un choix à `flag_requis`
 * que si le flag est posé. Le moteur revalide de toute façon.
 *
 * ⟦A-49⟧ **C'est l'écran le plus vu du jeu** : ~467 événements par partie (SPEC-PRODUCTEUR
 * §10.2), tous porteurs d'un choix. Une seconde gagnée ici vaut ~9 minutes de partie (§10.5) —
 * devant toute l'automatisation. D'où trois règles de forme, qui ne se négocient pas :
 *
 * 1. **Le premier choix est TOUJOURS au même pixel.** La fenêtre est ancrée en haut (et non
 *    centrée), et le bloc titre + texte réserve sa hauteur maximale. Avant, le premier bouton
 *    se posait sur 20 hauteurs différentes réparties sur 102 px : 467 fois par partie, l'œil
 *    et le curseur devaient le retrouver.
 * 2. **Tout ce qui se clique est dans la MÊME liste**, y compris le « Plus tard » d'une offre.
 *    Un bouton d'une autre famille, dans un autre coin, c'est une cible de plus à chercher.
 * 3. **Un chiffre par choix, et la touche qui va avec.** Trancher au clavier fait l'économie du
 *    trajet de souris depuis l'objet qu'on venait de cliquer.
 *
 * ⟦C57 · lot 2 · A-174⟧ **Moins de texte, et c'est ICI que ça se voit le plus.** Matt, 02/10/2026 :
 * *« on ne lit plus rien à la fin »* ; or cette fenêtre est le dialogue le plus répété du jeu (37
 * événements en 242 actions). À l'écran : un titre de 5 mots au plus, UNE situation de 12 mots au
 * plus, des choix de 6 mots au plus, la morale en 12 mots au plus. Ces textes courts sont écrits
 * À CÔTÉ des longs dans `content/events.json` (`titre_court`, `texte_court`, `label_court`,
 * `morale_courte`) : le long ne bouge pas. Tout ce que l'écran ne dit plus (la situation entière,
 * les choix en entier, la parade, l'avertissement de strike en toutes lettres) s'ouvre derrière
 * « Les dessous », et la situation, le choix et la morale rejoignent le Journal. Les règles de
 * forme d'⟦A-49⟧ tiennent : le bouton « Les dessous » est dans la ligne du titre, il ne pousse
 * jamais le premier choix, et le chiffre d'un choix ouvert ne fait rien tant que le dialogue est ouvert.
 * Les EFFETS des choix n'étaient pas télégraphiés (⟦SPEC-UI §3.6⟧) : ⟦A-179⟧ le lève, voir plus bas.
 *
 * ⟦C62 · lot événements · A-179⟧ **Un débutant doit comprendre cette fenêtre sans rien connaître du jeu.** Matt,
 * 21/10/2026 : *« Il faut que le jeu soit simple et limpide. »* Quatre changements, tous gouvernés par le
 * réglage de contenu `meta.evenements_ecran` (absent ⇒ l'écran d'avant, au caractère près) :
 *
 *  1. **Le bandeau dit un mot de joueur** (`libelleCategorie`) : « IMPRÉVU », plus « BAD BUZZ » ;
 *  2. **Les choix verrouillés attendent l'Arbre** : tant qu'il n'est pas ouvert, ni le 🔒 « nécessite : … » ni
 *     l'étiquette « parade » (ni « La parade » de « Les dessous ») n'existent : ils renvoient à un système que
 *     le joueur n'a pas encore ;
 *  3. **Chaque choix dit ce qu'il fait**, en chiffres et en icônes sous son libellé (`game/consequences.ts` :
 *     les écarts sont ceux du moteur, aucun chiffre n'est recopié), et la fenêtre « c'est réglé » les répète ;
 *  4. les textes courts du contenu présentent leurs personnages (« ton chef » : celui du resto où l'on bosse).
 *
 * Les règles de forme d'⟦A-49⟧ tiennent : la fenêtre reste ancrée, le premier choix ne bouge pas d'un pixel
 * (les pastilles sont dans le bouton, sous le libellé), un chiffre par choix, une seule liste de cibles.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  STRIKES_FATAL,
  flagPose,
  opportunitesLisibles,
  strikesActifs,
  type Effect,
  type EngineEvent,
  type EventChoice,
  type GameState,
} from "@maxime/shared";

import { contenu } from "../game/content";
import { consequencesDesEffets, type Consequence } from "../game/consequences";
import { ouvert } from "../game/escalier";
import {
  blocsApresChoix,
  blocsAvantChoix,
  iconeCategorie,
  labelCourt,
  libelleCategorie,
  moraleCourte,
  reglagesEvenementsEcran,
  strikeCourt,
  strikeLong,
  texteCourt,
  titreCourt,
} from "../game/evenementCourt";
import type { DetailJournal } from "../game/gameClient";
import { detailJournal } from "../game/journal";
import { interrupteurs } from "../game/menage";
import { demarrerMinuteurManager } from "../game/minuteurManager";
import { capacite, managerEnPoste } from "../game/moteur";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { DessousDuJournal } from "./DessousDuJournal";
import { Fenetre } from "./Fenetre";
import { IconeAsset } from "./IconeAsset";
import { MicroInteractionFormulaire } from "./MicroInteractionFormulaire";
import { useSfx } from "./SoundProvider";

/**
 * Combien de strikes ce choix pose-t-il ? Lu dans les effets du contenu (`strike_delta`,
 * SPEC-CONTENU §3.3), jamais deviné : c'est la seule façon d'être sûr que l'avertissement
 * affiché correspond à ce que le moteur va réellement faire. Un `value` négatif (un strike
 * retiré) n'est pas un avertissement — d'où le `Math.max(0, …)`.
 */
function strikesDuChoix(effets: readonly Effect[]): number {
  // ⟦C60 · A-177⟧ Les strikes sont coupés : `strike_delta` ne fait plus rien côté moteur, donc aucun
  // avertissement « ⛔ +1 strike » à promettre sur un bouton. Rallumés, le compte d'avant revient.
  if (!interrupteurs().strikes) return 0;
  return effets.reduce(
    (total, e) => (e.type === "strike_delta" ? total + Math.max(0, e.value) : total),
    0,
  );
}

/**
 * ⟦A-49⟧ Une ligne cliquable de la fenêtre — un choix du contenu, le « Plus tard » d'une offre,
 * ou ⟦A-50 §3 levier 1 · A-108⟧ « Je gère ». Les trois se ressemblent parce que, du point de vue
 * du joueur, ce sont des façons de répondre. `differer` sépare seulement le « Plus tard » d'une
 * offre (gratuit, ne parle pas au moteur) des deux autres (parlent au moteur, coûtent) — « Je
 * gère » a sa propre branche dans `trancher.current`, PAS un `choice_id` de contenu.
 */
interface Option {
  readonly cle: string;
  /** ⟦C57 · lot 2⟧ Le libellé AFFICHÉ : le court du contenu (`label_court`), sinon le long. Le long s'ouvre derrière « Les dessous ». */
  readonly label: string;
  readonly strikes: number;
  readonly differer: boolean;
  /**
   * ⟦SPEC-UI §3.6⟧ Ce que le contenu télégraphie de ce choix, ou `undefined` : « les effets
   * d'un choix ne sont affichés **que** s'ils sont fournis par l'API (`hint`) — le contenu
   * décide de ce qui est télégraphié ». L'écran ne lit JAMAIS `effets` pour le deviner : la
   * seule exception écrite est l'avertissement de strike ci-dessous, qui télégraphie une fin
   * de partie (le PLAN interdit l'échec non télégraphié — il gagne sur la discrétion).
   * ⟦C57 · lot 2⟧ Un indice ne s'écrit plus sur le bouton : il rejoint « Les dessous » (aucun choix
   * du contenu n'en porte aujourd'hui). Seul le formulaire de micro-interaction le reçoit encore.
   */
  readonly hint?: string;
}

/**
 * ⟦SPEC-UI §3.6⟧ Un choix **hors de portée** : affiché, grisé, avec sa condition.
 *
 * 🔑 **Pourquoi il fallait le montrer.** Cet écran les *filtrait* : un choix dont le
 * `skill_requis` n'était pas acquis n'existait tout simplement pas. 61 choix du contenu sont
 * dans ce cas, répartis sur 19 nœuds — c'est-à-dire 61 fois où le jeu avait quelque chose à
 * apprendre au joueur et se taisait. La perte n'est pas cosmétique, elle est de conception :
 * *une contre-mesure ne se découvre jamais, elle se possède ou elle n'existe pas.* Le joueur
 * subit une tuile, choisit la moins mauvaise réponse, et n'apprend **jamais** qu'il aurait pu
 * en avoir une bonne. Il ne peut donc pas s'améliorer — dans un jeu dont ⟦A-22⟧ fait de la
 * corrélation compétence → réussite un invariant non négociable.
 *
 * Le principe est déjà écrit ailleurs dans le projet, mot pour mot : la porte d'abonnés d'un
 * nœud d'arbre (`Skill.abonnes_min`, ⟦A-44/A-66⟧) laisse le nœud « **visible et grisé** avec sa
 * condition affichée — c'est une promesse, et une promesse se voit ⟦A-43⟧ ». Un choix
 * d'événement verrouillé est la même promesse, vue depuis l'autre bout.
 *
 * ⚠️ **Seul le `skill_requis` donne un choix grisé ; un `flag_requis` non tenu continue de
 * filtrer.** Ce n'est pas une demi-mesure, c'est la ligne d'⟦A-43⟧ : *« si une phrase pourrait
 * figurer telle quelle dans un wiki, elle n'a rien à faire dans le jeu »*. Un skill a un **nom
 * de jeu** que le joueur peut aller acheter — « nécessite : Gestion de crise » est une piste
 * jouable. Un flag est un état narratif (`monetise`, `algo_prepare`, `couple_expose`, six choix
 * en tout) : il n'a pas de nom, on ne peut pas aller le chercher, et l'afficher reviendrait à
 * exposer un identifiant de contenu. On dirait au joueur qu'il a raté quelque chose sans
 * pouvoir lui dire quoi — le pire des deux mondes.
 */
interface Verrouille {
  readonly cle: string;
  /** ⟦C57 · lot 2⟧ Le libellé AFFICHÉ (le court quand il existe) ; le long est dans `choix`. */
  readonly label: string;
  readonly choix: EventChoice;
  /** Le **nom** du nœud, jamais son id : `content/skills.json` est la seule source. */
  readonly nomDuNoeud: string;
}

/** La clé de l'option « Plus tard » — jamais envoyée au moteur, elle ne sort pas de l'écran. */
const PLUS_TARD = "__plus_tard";

/**
 * ⟦A-50 §3 levier 1 · A-108⟧ La clé de l'option « Je gère » — celle-là, contrairement à
 * `PLUS_TARD`, EST envoyée au moteur (`{ type: "reporter_evenement" }`) : elle ne ferme rien,
 * elle résout le `pending_event` sur son issue par défaut. Nom distinct exprès : la même
 * fenêtre porte déjà un « Plus tard » qui ne coûte rien (fermer une offre) — les confondre
 * ferait croire que reporter un événement bloquant est gratuit, ce qui est précisément faux.
 */
const JE_GERE = "__je_gere";

/**
 * ⟦A-49⟧ La phrase d'avertissement de strike, telle qu'affichée sur le bouton normal —
 * factorisée pour que le formulaire de micro-interaction (⟦A-4 décision 16⟧) la dise à
 * l'identique. `undefined` si le choix ne pose aucun strike : rien à dire, rien à rendre.
 */
function avertissementStrikes(option: Option, state: GameState): string | undefined {
  if (option.strikes <= 0) return undefined;
  // ⟦C57 · lot 2⟧ Une icône et des nombres à la place de la phrase : `⛔ +1 strike (2/3)`. La phrase entière
  // (`avertissementLong`) reste derrière « Les dessous », en infobulle et au lecteur d'écran.
  return strikeCourt(option.strikes, strikesActifs(state).length + option.strikes, STRIKES_FATAL);
}

/** ⟦C57 · lot 2⟧ L'avertissement de strike en toutes lettres : infobulle et lecteur d'écran du bouton. */
function avertissementLong(option: Option, state: GameState): string | undefined {
  if (option.strikes <= 0) return undefined;
  return `⛔ ${strikeLong(option.strikes, strikesActifs(state).length + option.strikes, STRIKES_FATAL)}`;
}

/** ⟦C57 · lot 2⟧ Le choix que le moteur a retenu pour cet événement (le joueur, « Je gère » ou le manager l'ont tranché). */
function choixResolu(events: readonly EngineEvent[], eventId: string): string | null {
  for (const e of events) {
    if (e.kind !== "event_resolved" || e.data.event_id !== eventId) continue;
    const id = e.data.choice_id;
    if (typeof id === "string") return id;
  }
  return null;
}

/**
 * ⟦C62 · lot événements · A-179⟧ Les conséquences d'un choix, ou rien. Une ligne de confort ne casse JAMAIS la
 * fenêtre qui bloque le jeu (ni la morale qui suit) : si le calcul échoue, le bouton est celui d'avant.
 */
function consequencesSures(state: GameState, effets: readonly Effect[], eventId: string): readonly Consequence[] {
  try {
    return consequencesDesEffets(state, effets, eventId);
  } catch {
    return [];
  }
}

/**
 * ⟦C62 · lot événements · A-179⟧ Les conséquences d'un choix, en une ligne de pastilles : le chiffre et son icône
 * (`−3 ❤`), vert quand c'est bon pour le joueur, rouge quand c'est mauvais. Les pastilles sont lues par
 * l'œil, pas par le lecteur d'écran : celui-ci reçoit la phrase entière (`Santé mentale : −3`), pas un
 * pictogramme.
 */
function ListeConsequences({ consequences }: { consequences: readonly Consequence[] }) {
  return (
    <span className="bouton-option-consequences">
      {consequences.map((c) => (
        <span key={c.cle + c.texte} className={`consequence consequence-${c.ton}`} title={c.phrase} aria-hidden="true">
          {c.texte}
        </span>
      ))}
      <span className="visuellement-cache">{consequences.map((c) => c.phrase).join(", ")}</span>
    </span>
  );
}

export function EvenementModal() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  // Un déverrouillage et un événement peuvent tomber sur la même action (publier ouvre la
  // veille ET tire une tuile). ⟦A-5⟧ le moment d'ouverture passe d'abord : deux modales
  // empilées, ce n'est plus un moment, c'est un embouteillage.
  const deverrouillages = useRunStore((s) => s.deverrouillagesEnAttente.length);
  const paliers = useRunStore((s) => s.paliersEnAttente.length);
  const chute = useRunStore((s) => s.chute);
  // ⟦A-9⟧ La leçon « il t'arrive quelque chose » doit se lire AVANT la première tuile —
  // sinon on explique l'événement au joueur une fois qu'il a déjà choisi.
  const decouverte = useDecouverteStore((s) => s.courante) !== null;
  // ⟦A-16⟧ La tuile arrive à la FIN de l'action : le récapitulatif de ce qui vient de se passer
  // se lit avant, sinon le joueur tranche un événement sans savoir où il en est.
  const recap = useRunStore((s) => s.recapEnAttente) !== null;
  const suspendus =
    useUIStore((s) => s.momentsSuspendus) ||
    deverrouillages > 0 ||
    paliers > 0 ||
    chute !== null ||
    decouverte ||
    recap;
  const premier = useRef<HTMLButtonElement>(null);
  const sfx = useSfx();
  /**
   * ⟦A-38⟧ **La morale** — la petite phrase du champ `morale` (SPEC-CONTENU §5bis.1), qui se dit
   * **après** le choix. Elle vit ici, en état d'interface : ⟦A-27⟧ l'a écrite dans le contenu et
   * l'a livrée sans écran (« le champ existe et porte les 8 phrases validées ; aucun écran ne
   * l'affiche encore »), ⟦A-34⟧ l'a laissée ouverte.
   *
   * Elle n'est posée que si le moteur a **accepté** le choix — sinon on commenterait une décision
   * qui n'a pas eu lieu.
   */
  const [morale, setMorale] = useState<{
    texte: string;
    icone: string;
    detail: DetailJournal | undefined;
    /** ⟦C62 · A-179⟧ Ce que le choix retenu a fait, en chiffres : la fenêtre « c'est réglé » n'en disait aucun. */
    consequences: readonly Consequence[];
  } | null>(null);

  const enAttente = run?.state.pending_event ?? null;
  // ⟦A-15 §4⟧ L'offre que le joueur vient de déplier depuis le bandeau des fenêtres ouvertes.
  // Le `pending_event` passe toujours devant : lui seul bloque le reste du jeu.
  const offreId = useUIStore((s) => s.offreOuverte);
  const fermerOffre = useUIStore((s) => s.fermerOffre);
  const offre =
    run && !enAttente && offreId !== null
      ? (opportunitesLisibles(run.state).find((o) => o.event_id === offreId) ?? null)
      : null;

  const eventId = enAttente?.event_id ?? offre?.event_id ?? null;
  /** Une offre se referme à la demande ; un `pending_event`, jamais (arbitrage A24). */
  const bloquant = enAttente !== null;

  const event = eventId !== null ? (contenu().parEvenement.get(eventId) ?? null) : null;
  const state = run?.state ?? null;

  // ⟦C8 · A-120⟧ Le minuteur RÉEL du manager — opt-in (défaut OFF), et seulement sur un
  // `pending_event` bloquant (jamais une offre, déjà ignorable gratuitement). Sans manager en
  // poste OU réglage éteint, rien de tout ceci n'existe : l'événement bloque comme avant
  // ⟦A-118 §1⟧, l'état neutre du chantier.
  const managerDelaiActif = useUIStore((s) => s.settings.managerDelaiActif);
  const [secondesRestantes, setSecondesRestantes] = useState<number | null>(null);
  /** Annule le minuteur en cours — appelée à la moindre interaction : « il a la main, toujours ». */
  const annulerMinuteur = useRef<() => void>(() => {});

  /**
   * ⟦A-49 règle 2⟧ **Une seule liste de cibles.** Les choix du contenu, plus — pour une offre —
   * le « Plus tard » qui, jusqu'ici, vivait seul en bas à droite dans une autre famille de
   * bouton. Laisser une tuile de côté EST une décision : elle a sa place dans la liste des
   * décisions, à la même abscisse que les autres, avec son chiffre.
   */
  /** Le nœud manque-t-il, et lui seul ? (le flag, lui, ne se dit pas — voir `Verrouille`.) */
  const noeudManquant = (c: { skill_requis?: string }): string | null =>
    c.skill_requis && state && !state.skills.includes(c.skill_requis) ? c.skill_requis : null;
  const flagManquant = (c: { flag_requis?: string }): boolean =>
    Boolean(c.flag_requis) && Boolean(state) && !flagPose(state!, c.flag_requis!);

  // ⟦A-53⟧ `choix` est optionnel depuis les événements-notification. Ceux-là ne posent jamais
  // de `pending_event` et n'arrivent donc jamais ici — le `?? []` est un filet de typage, pas
  // un cas de rendu.
  const choixDisponibles = (event?.choix ?? []).filter(
    (c) => noeudManquant(c) === null && !flagManquant(c),
  );
  /**
   * ⟦A-50 §3⟧ Le PREMIER choix disponible EST l'« issue par défaut » que le moteur applique
   * (`resoudreReportEvenement`, `events.ts`) — même filtre, même ordre, jamais recalculé côté
   * règle : seul le libellé du bouton diffère de celui du choix qu'il joue en coulisse.
   */
  const choixParDefaut = choixDisponibles[0] ?? null;

  const options: readonly Option[] =
    event && state
      ? [
          ...choixDisponibles.map((c) => ({
            cle: c.id,
            label: labelCourt(c),
            strikes: strikesDuChoix(c.effets),
            differer: false,
            hint: c.hint,
          })),
          ...(offre ? [{ cle: PLUS_TARD, label: "Plus tard", strikes: 0, differer: true }] : []),
          // ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » : visible SEULEMENT sur un `pending_event`
          // bloquant (jamais une offre, déjà ignorable gratuitement) et SEULEMENT avec la
          // capacité — sans elle, le bouton n'existe pas plutôt que d'apparaître grisé
          // (⟦A-5⟧ : ce qui n'est pas déverrouillé est absent, pas grisé). Les `strikes`
          // affichés sont ceux du choix par défaut : le bouton ne peut pas promettre moins de
          // risque que ce qu'il joue réellement.
          //
          // `differer: false` bien que la place et le geste rappellent « Plus tard » : celui-là
          // ne parle jamais au moteur (il referme, gratuit) — « Je gère » COÛTE (l'issue par
          // défaut) et doit donc suivre le chemin normal (numéro clavier compris), pas le
          // raccourci de fermeture.
          ...(bloquant && choixParDefaut && capacite(state, "reporter_evenement")
            ? [
                {
                  cle: JE_GERE,
                  label: "Je gère",
                  strikes: strikesDuChoix(choixParDefaut.effets),
                  differer: false,
                },
              ]
            : []),
        ]
      : [];

  /**
   * ⟦SPEC-UI §3.6⟧ Les choix hors de portée, **après** la liste des cibles et sans chiffre.
   *
   * ⚠️ **La place et l'absence de numéro sont l'arbitrage, pas un détail de mise en page.**
   * ⟦A-49⟧ tient trois règles de forme sur ce modal, vu ~467 fois par partie : *premier choix
   * toujours au même pixel*, *une seule liste de cibles*, *un chiffre par choix*. Intercaler
   * des tuiles grisées **dans** la liste violerait les trois d'un coup — la première cible
   * bougerait selon les nœuds possédés, et un chiffre sur deux ne ferait rien. Les verrouillés
   * vivent donc **sous** la liste : ce qu'on peut faire ne bouge pas d'un pixel, ce qu'on ne
   * peut pas faire se lit après, et aucune touche ne mène nulle part.
   */
  // ⟦C62 · lot événements · A-179⟧ **Tant que l'Arbre n'est pas ouvert, il n'y a pas de choix verrouillé.** Un
  // « 🔒 nécessite : Monteur salarié » renvoie à un panneau de liège que le joueur n'a pas encore : trois
  // événements en trois vidéos, trois promesses incompréhensibles (audit du 21/10). Le réglage de contenu
  // `evenements_ecran.masquer_verrouilles_avant_arbre` (éteint ⇒ l'écran d'avant) les retire, ainsi que
  // l'étiquette « parade » et « La parade » de « Les dessous » : le même système, vu de l'autre bout.
  const reglages = reglagesEvenementsEcran();
  const masquerVerrouilles = reglages.masquerVerrouillesAvantArbre && !(state !== null && ouvert(state, "arbre"));

  const verrouilles: readonly Verrouille[] =
    event && state && !masquerVerrouilles
      ? (event.choix ?? []).flatMap((c) => {
          const skill = noeudManquant(c);
          // Un choix bloqué AUSSI par un flag reste caché : sa condition serait à moitié
          // indicible, et une promesse à moitié dite est une fausse piste.
          if (skill === null || flagManquant(c)) return [];
          return [
            {
              cle: c.id,
              label: labelCourt(c),
              choix: c,
              nomDuNoeud: contenu().parSkill.get(skill)?.nom ?? skill,
            },
          ];
        })
      : [];

  /**
   * ⟦C62 · lot événements · A-179⟧ **Ce que chaque choix fait, en chiffres et en icônes** (`game/consequences.ts` : les
   * écarts viennent du moteur, rien n'est recopié). Calculé une fois par événement et par état, pas à chaque
   * rendu : c'est une copie de l'état par choix. Réglage éteint ⇒ aucune conséquence, le bouton d'avant.
   */
  const consequences = useMemo(() => {
    const parChoix = new Map<string, readonly Consequence[]>();
    if (!reglages.consequencesVisibles || !event || !state) return parChoix;
    for (const c of event.choix ?? []) parChoix.set(c.id, consequencesSures(state, c.effets, event.id));
    return parChoix;
  }, [reglages.consequencesVisibles, event, state]);

  /**
   * Le geste qu'un chiffre déclenche, à jour à chaque rendu. Il passe par une référence pour
   * que l'abonnement clavier ne se refasse pas à chaque frame : la liste des options est un
   * tableau neuf à chaque rendu, elle ne peut pas servir de dépendance.
   */
  /**
   * ⟦A-4 décision 16 · SPEC-CONTENU §5bis.2⟧ La micro-interaction, quand le contenu en porte
   * une : le dernier champ du formulaire (`MicroInteractionFormulaire`) affiche les MÊMES
   * `options` que la liste normale — jamais un mapping séparé à tenir à jour — et sa réponse
   * appelle `trancher.current` par INDEX, comme un clic sur le bouton normal. Absent, ce
   * bloc ne change rien : l'état neutre ⟦A-10⟧ est le rendu d'aujourd'hui.
   */
  const microInteraction = event?.micro_interaction;

  /**
   * ⟦A-38 · C57 lot 2⟧ La fenêtre « c'est réglé » : la morale COURTE (12 mots au plus) à l'écran, la situation, le
   * choix retenu et la morale longue derrière « Les dessous » (et au Journal, par `game/journal.ts`). Sans morale
   * dans le contenu, rien ne se dit : l'état neutre.
   */
  const poserMorale = (e: NonNullable<typeof event>, events: readonly EngineEvent[], avant: GameState | null) => {
    const texte = moraleCourte(e);
    if (texte === undefined) return;
    const retenu = choixResolu(events, e.id);
    // ⟦C62 · A-179⟧ Ce que le choix RETENU a fait (lu sur l'état d'AVANT la réponse : celui d'après a déjà bougé).
    const effets = retenu !== null ? (e.choix ?? []).find((c) => c.id === retenu)?.effets : undefined;
    setMorale({
      texte,
      icone: iconeCategorie(e.categorie),
      detail: detailJournal(e.nom, blocsApresChoix(e, choixResolu(events, e.id))),
      consequences:
        reglages.consequencesVisibles && avant !== null && effets !== undefined
          ? consequencesSures(avant, effets, e.id)
          : [],
    });
  };

  const trancher = useRef<(index: number) => void>(() => {});
  trancher.current = (index: number) => {
    // ⟦C8 §4⟧ Toute interaction du joueur avec la modale annule le minuteur du manager — il a
    // la main, toujours.
    annulerMinuteur.current();
    const option = options[index];
    if (!option || pending || !event) return;
    if (option.differer) {
      fermerOffre();
      return;
    }
    sfx(event.negatif ? "bad_buzz_sting" : "good_buzz_sting");
    // Répondre referme la fenêtre côté moteur ; on range l'état d'interface avec,
    // pour qu'un id mort ne traîne pas jusqu'à la prochaine offre.
    fermerOffre();
    // ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » n'est PAS un `choix_evenement` : `JE_GERE` n'est
    // l'id d'aucun choix du contenu, l'action dédiée retrouve le `pending_event` elle-même
    // côté moteur. Même post-traitement que les autres (son, morale) — c'est bien l'événement
    // qui s'est résolu, juste pas sur le choix du joueur.
    const jouer =
      option.cle === JE_GERE
        ? act({ type: "reporter_evenement" })
        : act({ type: "choix_evenement", event_id: event.id, choice_id: option.cle });
    void jouer.then((resultat) => {
      // ⟦A-38⟧ Le moteur a tranché : la phrase peut tomber. S'il a refusé (`null`), rien ne
      // se dit — on ne commente pas un choix qui n'a pas eu lieu.
      if (resultat) poserMorale(event, resultat.events, state);
    });
  };

  /**
   * ⟦C8 · A-120⟧ Le minuteur a expiré : le manager tranche, sans que le joueur ait rien
   * cliqué. Même post-traitement que `trancher.current` (son, morale) — c'est bien
   * l'événement qui s'est résolu, juste pas sur le choix du joueur, ni même un choix que le
   * client connaît à l'avance (c'est le moteur qui décide, `resoudreTrancheParManager`).
   */
  const trancherViaManager = useRef<() => void>(() => {});
  trancherViaManager.current = () => {
    if (pending || !event) return;
    sfx(event.negatif ? "bad_buzz_sting" : "good_buzz_sting");
    const jouer = act({ type: "trancher_par_manager" });
    void jouer.then((resultat) => {
      if (resultat) poserMorale(event, resultat.events, state);
    });
  };

  /**
   * ⟦C8 · A-120⟧ **Le compte à rebours réel.** Démarre UNE fois par `pending_event`, jamais
   * réaffecté par un re-rendu de l'écran (dépendances limitées à `eventId`/`bloquant`) : sans
   * quoi n'importe quelle mise à jour de l'état de la run relancerait le minuteur. Sans
   * manager en poste, ou réglage éteint, il ne démarre jamais — l'événement bloque comme
   * aujourd'hui ⟦A-118 §1⟧.
   */
  useEffect(() => {
    // ⟦A-9⟧ Un moment suspendu (décharge, palier, récap…) met la pop-up d'événement en pause :
    // le minuteur ne doit pas expirer DERRIÈRE un autre dialogue — on ne le démarre pas tant
    // qu'un moment le précède, quitte à repartir de zéro une fois la voie libre.
    if (!bloquant || !managerDelaiActif || !state || suspendus) return;
    if (!managerEnPoste(state)) return;
    const annuler = demarrerMinuteurManager({
      onTick: setSecondesRestantes,
      onExpire: () => trancherViaManager.current(),
    });
    annulerMinuteur.current = annuler;
    return () => {
      annuler();
      annulerMinuteur.current = () => {};
      setSecondesRestantes(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- volontaire : voir le commentaire.
  }, [eventId, bloquant, managerDelaiActif, suspendus]);

  useEffect(() => {
    if (eventId === null || suspendus) return;
    premier.current?.focus();
    sfx("event_notif");
  }, [eventId, suspendus, sfx]);

  /**
   * ⟦A-49 règle 3⟧ **Les chiffres.** La tuile arrive après un clic posé ailleurs dans la pièce
   * (le lit, le bureau) : atteindre le bouton, c'est un trajet de souris **plus** la recherche
   * de la cible. Une touche supprime les deux. Entrée reste branchée nativement sur le bouton
   * qui a le focus — le premier à l'ouverture, donc « Entrée » vaut « 1 ».
   *
   * Le champ de saisie est respecté : la télécommande de dev (`?dev=1`) a des `<input>`, et un
   * « 3 » tapé dedans doit rester un « 3 ».
   */
  useEffect(() => {
    // ⟦A-4 décision 16⟧ Le raccourci chiffré vise le bouton normal ; en micro-interaction, la
    // cible du clavier est le formulaire (radios, bouton « Champ suivant ») — un « 1 » perdu
    // ne doit pas résoudre l'événement avant le dernier champ.
    if (eventId === null || suspendus || morale || microInteraction) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const cible = e.target as HTMLElement | null;
      if (cible?.isContentEditable) return;
      if (cible && /^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName)) return;
      // ⟦C57 · lot 2⟧ « Les dessous » est ouvert : on LIT, on ne tranche pas (le chiffre ne fait rien).
      if (document.querySelector(".overlay-dessous")) return;
      if (!/^[1-9]$/.test(e.key)) return;
      e.preventDefault();
      trancher.current(Number(e.key) - 1);
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [eventId, suspendus, morale, microInteraction]);

  useEffect(() => {
    if (eventId === null || suspendus || bloquant) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        fermerOffre();
      }
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [eventId, suspendus, bloquant, fermerOffre]);

  // La morale se referme d'Échap comme d'un clic : elle ne bloque rien, elle se lit.
  useEffect(() => {
    if (!morale || suspendus) return;
    const auClavier = (e: KeyboardEvent) => {
      // ⟦C57 · lot 2⟧ « Les dessous » ouvert : Entrée y sert son propre bouton, elle ne referme pas la morale derrière.
      if (document.querySelector(".overlay-dessous")) return;
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        setMorale(null);
      }
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [morale, suspendus]);

  /**
   * ⟦A-38⟧ La morale passe **après** les grands moments (récapitulatif, palier, déverrouillage,
   * chute) et **avant** l'événement suivant : c'est la fin de celui qu'on vient de trancher.
   * Elle attend son tour sans se perdre — `suspendus` la met en pause, il ne l'efface pas.
   */
  if (run && morale && !suspendus) {
    // ⟦C48 · A-168 §3⟧ La morale est une petite `Fenetre` et non plus une carte crème penchée de 0,6°. Le
    // bouton est dans le pied, avec le focus posé dessus : Entrée la referme, comme avant.
    // ⟦C57 · lot 2⟧ Le bandeau dit seulement « C'est réglé » (le titre de l'événement tenait à lui seul jusqu'à
    // huit mots) ; la morale courte est à l'écran, « Les dessous » porte la situation, le choix et la morale longue.
    return (
      <div className="overlay" role="presentation">
        <div className="dialogue-fenetre dialogue-fenetre-etroite">
          <Fenetre
            titre="C'est réglé"
            icone={morale.icone}
            role="dialog"
            ariaModal
            ariaLabelledBy="morale-phrase"
            className="fenetre-dialogue fenetre-morale"
            pied={
              <>
                {morale.detail ? <DessousDuJournal detail={morale.detail} variante="texte" /> : null}
                {/* Le focus part dans la carte dès qu'elle s'ouvre — règle des dialogues maison
                    (SPEC-UI §7.1), et c'est ce qui rend Entrée utilisable pour la refermer. */}
                <button type="button" className="bouton primaire" autoFocus onClick={() => setMorale(null)}>
                  On continue
                </button>
              </>
            }
          >
            <p id="morale-phrase" className="morale-phrase">
              {morale.texte}
            </p>
            {/* ⟦C62 · A-179⟧ Ce que le choix a fait, en chiffres : la fenêtre n'en disait aucun. */}
            {morale.consequences.length > 0 ? <ListeConsequences consequences={morale.consequences} /> : null}
          </Fenetre>
        </div>
      </div>
    );
  }

  if (!run || !state || eventId === null || !event || suspendus) return null;

  const parade =
    event.contremesure !== undefined && !state.skills.includes(event.contremesure.skill_id);
  // ⟦C62 · A-179⟧ La parade est un nœud de l'Arbre : tant qu'il est fermé, ni l'étiquette ni le bloc « La parade » de
  // « Les dessous » n'existent (voir `masquerVerrouilles`).
  const { contremesure: _parade, ...evenementSansParade } = event;
  const evenementAffiche = masquerVerrouilles ? evenementSansParade : event;

  // ⟦C57 · lot 2 · A-174⟧ « Les dessous » : la situation entière, les choix en entier (indices, avertissement de
  // strike en toutes lettres), la parade. `undefined` quand le contenu est déjà court partout et sans parade :
  // jamais un bouton qui s'ouvre sur du vide.
  const dessous = detailJournal(
    event.nom,
    blocsAvantChoix(evenementAffiche, {
      disponibles: choixDisponibles,
      verrouilles: verrouilles.map((v) => ({ choix: v.choix, nomDuNoeud: v.nomDuNoeud })),
      strikes: new Map(choixDisponibles.map((c) => [c.id, strikesDuChoix(c.effets)])),
      strikesActifs: strikesActifs(state).length,
      paradePossedee: event.contremesure !== undefined && !parade,
    }),
  );

  // ⟦C48 · A-168 §3⟧ **La fenêtre d'événement est une `Fenetre`** : la catégorie et les étiquettes vont
  // dans le bandeau violet (rouge sombre quand l'événement est négatif), le nom de l'événement et
  // ses choix dans le corps tramé. Elle est la surface la plus vue du jeu (467 fois par partie) :
  // les règles de forme d'⟦A-49⟧ tiennent — bandeau à hauteur FIXE (`.fenetre-evenement`,
  // `app.css`), bande d'illustration fixe, exposé à hauteur réservée, fenêtre ancrée à 25 vh —, donc
  // le premier bouton ne bouge pas d'un événement à l'autre (mesuré avant/après, voir C48).
  const badges = (
    <>
      {event.major ? <span className="fenetre-badge">événement majeur</span> : null}
      {/* ⟦A-43 / A-49⟧ La contre-mesure manquante était une phrase de bas de fenêtre, lue sur 58
          événements sur 101 (« Il existe un nœud d'arbre qui adoucit ce genre de tuile. Tu ne
          l'as pas. »). Elle disait où chercher plus tard, jamais quoi faire maintenant : c'est une
          entrée de guide, versée telle quelle au brouillon MeTube. Reste le fait, en deux mots et
          à place fixe — l'œil l'apprend une fois. */}
      {parade && !masquerVerrouilles ? <span className="fenetre-badge">🛡 parade non débloquée</span> : null}
      {/* ⟦A-15 §4⟧ Le temps qui reste vient de `opportunitesLisibles` : c'est le moteur qui date la
          fermeture, l'écran ne fait que la dire. */}
      {offre ? (
        <span className={`fenetre-badge${offre.bientot_fermee ? " urgent" : ""}`}>
          {offre.heures_restantes <= 0
            ? "fenêtre fermée"
            : offre.jours_restants >= 1
              ? `encore ${offre.jours_restants} jour${offre.jours_restants > 1 ? "s" : ""}`
              : `encore ${Math.round(offre.heures_restantes)} h`}
        </span>
      ) : null}
      {/* ⟦C8 · A-120⟧ Le compte à rebours réel du manager — visible seulement quand le minuteur
          tourne (réglage actif + manager en poste). Temps RÉEL, pas simulé : il continue même
          immobile, contrairement à l'horloge de la chambre. */}
      {secondesRestantes !== null ? (
        <span className={`fenetre-badge${secondesRestantes <= 10 ? " urgent" : ""}`}>
          ⏳ manager : {secondesRestantes} s
        </span>
      ) : null}
    </>
  );

  return (
    <div className="overlay overlay-evenement" role="presentation">
      <Fenetre
        titre={libelleCategorie(event.categorie)}
        icone={iconeCategorie(event.categorie)}
        actions={badges}
        role="dialog"
        ariaModal
        ariaLabelledBy="evt-titre"
        className={`fenetre-dialogue fenetre-evenement ${event.negatif ? "negatif" : "positif"}`}
      >
        {/* ⟦C15 · A-135 · A-136⟧ L'illustration de l'événement — 30 des 140 events ont une
            planche (`assets/img-src/evenement/event-<id>.png`). Règle 1 oblige (juste
            au-dessus) : cette bande a une hauteur FIXE que l'image existe ou non — sinon les
            110 événements sans illustration décaleraient le premier bouton par rapport aux 30
            qui en ont une, exactement le bug que la hauteur fixe du bandeau corrige déjà pour
            l'emoji de catégorie. `IconeAsset` réutilise donc l'emoji de catégorie comme
            « cadre neutre » : jamais un rectangle vide, jamais un écran qui varie. */}
        <div className="evenement-illustration">
          <IconeAsset
            src={`/evenement/event-${event.id}.png`}
            loadingLazy={false}
            repli={
              <span className="evenement-illustration-repli" aria-hidden="true">
                {iconeCategorie(event.categorie)}
              </span>
            }
          />
        </div>

        {/* ⟦A-49 règle 1⟧ Le bloc qui expose la situation réserve sa hauteur maximale (trois
            lignes de texte, la plus longue des 101). C'est lui qui fait que la liste des choix
            commence toujours au même pixel — le prix en est un peu d'air sous les tuiles
            courtes, et il est dérisoire devant 467 recherches de cible par partie. */}
        <div className="evenement-expose">
          {/* ⟦C57 · lot 2 · A-174⟧ Le titre court (5 mots au plus), la situation courte (12 mots au plus), et
              « Les dessous » dans la ligne du titre : la hauteur réservée ne bouge pas, le premier choix non plus. */}
          <div className="evenement-tete">
            <h2 id="evt-titre" className="dialogue-titre">
              {titreCourt(event)}
            </h2>
            {dessous ? <DessousDuJournal detail={dessous} variante="texte" className="evenement-dessous" /> : null}
          </div>
          <p className="dialogue-corps">{texteCourt(event)}</p>
        </div>

        {microInteraction ? (
          // ⟦A-4 décision 16 · SPEC-CONTENU §5bis.2⟧ Le formulaire remplace la liste de
          // boutons — MÊMES `options`, donc mêmes effets, même refus moteur possible.
          <MicroInteractionFormulaire
            entete={microInteraction.entete}
            decor={microInteraction.decor}
            optionsFinales={options
              .filter((o) => !o.differer)
              .map((o) => ({
                id: o.cle,
                label: o.label,
                hint: o.hint,
                avertissement: avertissementStrikes(o, state),
              }))}
            disabled={pending}
            onValider={(id) => {
              const index = options.findIndex((o) => o.cle === id);
              if (index >= 0) trancher.current(index);
            }}
          />
        ) : (
          <div className="dialogue-options">
            {options.map((option, i) => {
              // ⟦C62 · A-179⟧ « Je gère » joue l'issue par défaut (le premier choix disponible) : il en dit donc les
              // conséquences. « Plus tard » ne fait rien : il n'en dit aucune.
              const conseq = option.differer
                ? undefined
                : consequences.get(option.cle === JE_GERE ? (choixParDefaut?.id ?? "") : option.cle);
              return (
                <button
                  key={option.cle}
                  type="button"
                  ref={i === 0 ? premier : undefined}
                  className={`bouton bouton-option${option.differer ? " bouton-option-differer" : ""}`}
                  disabled={pending}
                  aria-keyshortcuts={String(i + 1)}
                  onClick={() => trancher.current(i)}
                >
                  <span className="bouton-option-touche" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="bouton-option-label">{option.label}</span>
                  {/* ⟦C62 · lot événements · A-179⟧ Ce que le choix FAIT, sous son libellé : `−3 ❤ · +20 👥`. Les écarts sont
                      ceux du moteur (`game/consequences.ts`), la couleur dit si c'est bon ou mauvais pour le joueur. */}
                  {conseq && conseq.length > 0 ? <ListeConsequences consequences={conseq} /> : null}
                  {/* ⟦SPEC-UI §3.6⟧ Le `hint` du contenu (aucun choix d'aujourd'hui n'en porte) :
                      ⟦C57 · lot 2⟧ il se lit derrière « Les dessous », plus sur le bouton. */}
                  {/* « Prévenir, jamais surprendre » : « Encaisser le strike » ne dit nulle part
                      qu'à trois strikes la chaîne saute. Le joueur cliquait vers une fin de
                      partie sans le savoir — c'est exactement l'échec NON télégraphié que le
                      PLAN interdit. On lit l'effet du choix et l'état, on ne calcule rien.
                      ⟦A-49⟧ C'était la SEULE conséquence écrite sur un bouton ; ⟦A-179⟧ en ajoute d'autres,
                      en chiffres et en icônes (juste au-dessus), jamais en phrases : une phrase de plus ici
                      se paierait 467 fois. */}
                  {avertissementStrikes(option, state) ? (
                    <span
                      className="bouton-option-avertissement"
                      title={avertissementLong(option, state)}
                    >
                      {avertissementStrikes(option, state)}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {/* ⟦SPEC-UI §3.6⟧ « Un choix indisponible s'affiche grisé avec sa condition. »
            Hors du flux de tabulation (`disabled`) et sans chiffre : on les LIT, on ne les
            vise pas. C'est le seul endroit du jeu où le joueur apprend qu'une autre réponse
            existait — d'où le 🛡, le même que la contre-mesure du bandeau.
            ⟦A-4 décision 16⟧ Absent en micro-interaction : le formulaire ne montre que la
            question, jamais un choix qu'il n'a pas — le contenu témoin n'en verrouille aucun. */}
        {microInteraction || verrouilles.length === 0 ? null : (
          <div className="dialogue-options">
            {verrouilles.map((v) => (
              <button
                key={v.cle}
                type="button"
                className="bouton bouton-option bouton-option-verrouille"
                disabled
                tabIndex={-1}
                aria-disabled="true"
              >
                <span className="bouton-option-touche" aria-hidden="true">
                  🔒
                </span>
                <span className="bouton-option-label">{v.label}</span>
                <span className="bouton-option-condition">🛡 nécessite : {v.nomDuNoeud}</span>
              </button>
            ))}
          </div>
        )}
      </Fenetre>
    </div>
  );
}
